import { CardShell } from '@atlas/ui-components'
import { Badge, Skeleton } from '@atlas/ui-primitives'
import { ArticleTranslationBadge } from '@/components/articles/ArticleTranslationBadge'
import { ARTICLE_STATUS_LABELS, ARTICLE_TYPE_LABELS, type Article } from '@/domain/article.types'
import { isTranslationStuck } from '@/hooks/useArticles'
import { relativeTime } from '@/lib/labels'

export interface ArticleResultListProps {
	articles: Article[]
	onOpen: (articleId: string) => void
}

/** One article's row. Opens the composer — the only detail surface an author has. */
function ArticleRow({ article, onOpen }: { article: Article; onOpen: () => void }) {
	return (
		<button
			type="button"
			onClick={onOpen}
			className="flex w-full items-center gap-3 rounded-ds-card border border-ds-border bg-ds-surface-elevated p-4 text-left hover:border-ds-border-strong"
		>
			<div className="min-w-0 flex-1">
				<p className="truncate font-medium text-ds-text">{article.title}</p>
				{article.summary ? <p className="truncate text-sm text-ds-text-muted">{article.summary}</p> : null}
			</div>
			<Badge>{ARTICLE_TYPE_LABELS[article.articleType]}</Badge>
			<Badge variant="secondary">{article.languageIso}</Badge>
			{/* Drawn from the row's own field, so a translated variant's job state is
			    visible without a per-row group read — the index shows up to 100 rows. */}
			<ArticleTranslationBadge
				status={article.translationStatus}
				isStuck={isTranslationStuck(article.translationStatus, article.updatedAt)}
			/>
			<span className="text-xs uppercase tracking-wide text-ds-text-subtle">
				{ARTICLE_STATUS_LABELS[article.status]}
			</span>
			<span className="w-24 shrink-0 text-right text-xs text-ds-text-subtle">{relativeTime(article.updatedAt)}</span>
		</button>
	)
}

/** The index's results. Presentational — it fetches nothing and owns no filter state. */
export function ArticleResultList({ articles, onOpen }: ArticleResultListProps) {
	return (
		<div className="space-y-2">
			{articles.map((article) => (
				<ArticleRow key={article.articleId} article={article} onOpen={() => onOpen(article.articleId)} />
			))}
		</div>
	)
}

/**
 * The list before it arrives: rows of the same height, so the results land where
 * the placeholders stood instead of shifting the page.
 */
export function ArticleResultListSkeleton({ rows = 4 }: { rows?: number }) {
	return (
		<div className="space-y-2" aria-busy="true">
			<span className="sr-only">Loading articles…</span>
			{Array.from({ length: rows }, (_, index) => (
				<CardShell key={index} className="p-4">
					<Skeleton shape="text" className="h-5 w-1/3" />
					<Skeleton shape="text" className="mt-2 h-4 w-2/3" />
				</CardShell>
			))}
		</div>
	)
}
